import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ currentView, onNavigate, onNewPlan, isCollapsed, onToggleCollapse, isCreatingPlan = false, onShowPrivacy, onShowTerms, onSignOut, autoCollapseOnNavigate = false, onAutoCollapse }) => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Close user menu when clicking outside
  React.useEffect(() => {
    if (!showUserMenu) return;

    const handleClickOutside = (e) => {
      const target = e.target;
      const userMenuDropdown = target.closest('.user-menu-dropdown');
      const profileButton = target.closest('.profile-button');

      // If clicking the profile button, don't close here - let the onClick toggle handle it
      if (profileButton) {
        return;
      }

      // If clicking outside both button and dropdown, close
      if (!userMenuDropdown) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const getIcon = (id) => {
    const iconClass = "w-5 h-5";
    switch (id) {
      case 'home':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        );
      case 'dashboard':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'plans':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'shared':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'settings':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'help':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.25 9.75a3.75 3.75 0 117.18 1.52c-.47.88-1.32 1.28-2.1 1.72-.74.42-1.33.84-1.33 1.76M12 18h.01M12 22a10 10 0 110-20 10 10 0 010 20z" />
          </svg>
        );
      case 'signin':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
        );
      case 'signup':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', shortcut: 'D' },
    { id: 'shared', label: 'Shared', shortcut: 'S' },
  ];

  const guestNavigationItems = [
    { id: 'home', label: 'Home', shortcut: 'H' },
    { id: 'signin', label: 'Sign In', shortcut: 'I' },
    { id: 'signup', label: 'Sign Up', shortcut: 'U' },
  ];

  const CollapsedTooltip = ({ label }) => (
    isCollapsed ? (
      <span className="pointer-events-none absolute left-full top-1/2 z-[80] ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-900 opacity-0 shadow-lg ring-1 ring-black/10 transition group-hover:opacity-100 group-focus-visible:opacity-100">
        {label}
      </span>
    ) : null
  );

  const handleNavClick = (itemIdOrAction) => {
    const shouldAutoCollapse =
      autoCollapseOnNavigate || typeof itemIdOrAction === 'function';

    if (shouldAutoCollapse && onAutoCollapse) {
      onAutoCollapse();
    }

    if (typeof itemIdOrAction === 'function') {
      itemIdOrAction();
    } else if (itemIdOrAction === 'signin' && onNavigate) {
      onNavigate('signin');
    } else if (itemIdOrAction === 'signup' && onNavigate) {
      onNavigate('signup');
    } else {
      // Map 'home' to 'dashboard' logic for authenticated users
      const target = itemIdOrAction === 'home' ? (isAuthenticated ? 'dashboard' : 'home') : itemIdOrAction;
      if (onNavigate) {
        onNavigate(target);
      }
    }

    if (window.innerWidth < 768 && onToggleCollapse) {
      onToggleCollapse(); // Close drawer on mobile after navigation
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {!isCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-[54] md:hidden"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onToggleCollapse) {
              onToggleCollapse();
            }
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed ${isCreatingPlan && !isCollapsed ? 'md:fixed' : 'md:sticky'} top-0 left-0 h-screen z-[55] ${isCreatingPlan && !isCollapsed ? 'md:z-[60]' : 'md:z-[45]'}
          ds-sidebar
          flex flex-col overflow-x-hidden
          transition-all duration-300 ease-in-out
          ${isCollapsed ? '-translate-x-full md:translate-x-0 md:w-16' : 'translate-x-0 w-64'}
          ${isCreatingPlan && !isCollapsed ? 'shadow-2xl' : isCollapsed ? 'shadow-xl md:shadow-none' : 'shadow-xl'}
        `}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => {
          if (isCollapsed && window.innerWidth >= 1024) e.stopPropagation();
        }}
      >
        {/* Logo Section */}
        <div className="flex items-center justify-between h-16 px-4 overflow-hidden" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {!isCollapsed ? (
            <>
              <button
                onClick={() => handleNavClick('dashboard')}
                className="hover:opacity-80 transition-opacity focus:outline-none"
                title="Go to Dashboard"
              >
                <img src="/new_icon+text_white.png" alt="CraftMyParty" className="h-12 w-auto" style={{ maxWidth: '200px', objectFit: 'contain', opacity: 0.9 }} />
              </button>
              <button
                onClick={onToggleCollapse}
                className="p-2 rounded-lg transition focus:outline-none focus:ring-0"
                style={{ color: 'rgba(255,255,255,0.45)' }}
                onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.85)'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                title="Collapse sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <button
                onClick={onToggleCollapse}
                className="w-full h-full flex items-center justify-center transition focus:outline-none focus:ring-0"
                style={{ color: 'rgba(255,255,255,0.45)' }}
                onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.85)'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                title="Expand sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* New Plan Button */}
        {isCreatingPlan ? (
          <div
            className="p-4 relative z-50 overflow-hidden"
            style={{
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              visibility: 'hidden',
              pointerEvents: 'none'
            }}
            aria-hidden="true"
          >
            <button
              className={`
                w-full flex items-center justify-center gap-2
                bg-stone-100 text-zinc-900 hover:bg-white
                font-bold ring-1 ring-stone-300/60
                group relative
                ${isCollapsed ? 'px-2 py-2.5 rounded-xl' : 'px-4 py-2.5 rounded-xl'}
                focus:outline-none focus:ring-0
                transition-colors duration-150
              `}
              tabIndex={-1}
              aria-hidden="true"
            >
              <svg
                className={`flex-shrink-0 ${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-zinc-900`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              <CollapsedTooltip label="New Plan" />
              {!isCollapsed && <span className="whitespace-nowrap">New Plan</span>}
            </button>
          </div>
        ) : (
          <div className="p-4 relative z-50 overflow-hidden" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onNewPlan) onNewPlan();
              }}
              className={`
                w-full flex items-center justify-center gap-2
                bg-stone-100 text-zinc-900 hover:bg-white
                font-bold ring-1 ring-stone-300/60
                group relative
                ${isCollapsed ? 'px-2 py-2.5 rounded-xl' : 'px-4 py-2.5 rounded-xl'}
                focus:outline-none focus:ring-0
                transition-colors duration-150
              `}
              title={isCollapsed ? 'New Plan' : ''}
              aria-label="New Plan"
            >
              <svg
                className={`flex-shrink-0 ${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-zinc-900`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              <CollapsedTooltip label="New Plan" />
              {!isCollapsed && <span className="whitespace-nowrap">New Plan</span>}
            </button>
          </div>
        )}

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 min-h-0">
          <div className="space-y-0.5 px-2">
            {(isAuthenticated ? navigationItems : guestNavigationItems).map((item) => {
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    handleNavClick(item.id);
                  }}
                  className={`
                    ds-sidebar-item
                    w-full flex items-center gap-3 py-2.5
                    group relative z-50
                    ${isActive ? 'active' : ''}
                    ${isCollapsed ? 'pl-5' : 'pl-5 pr-3'}
                  `}
                  title={isCollapsed ? item.label : undefined}
                  aria-label={item.label}
                >
                  <span className="flex-shrink-0">{getIcon(item.id)}</span>
                  <CollapsedTooltip label={item.label} />
                  {!isCollapsed && (
                    <>
                      <span className="flex-1 text-left whitespace-nowrap overflow-hidden">{item.label}</span>
                      <span className="text-xs opacity-30 group-hover:opacity-50">{item.shortcut}</span>
                    </>
                  )}
                </button>
              );
            })}

            {/* Privacy & Terms */}
            {!isCollapsed && (
              <>
                <div className="my-3 mx-2 ds-sidebar-divider border-t" />
                {onShowPrivacy && (
                  <button
                    onClick={() => handleNavClick(() => onShowPrivacy())}
                    className="ds-sidebar-item w-full text-left px-3 py-2 text-sm rounded-lg whitespace-nowrap"
                  >
                    Privacy Policy
                  </button>
                )}
                {onShowTerms && (
                  <button
                    onClick={() => handleNavClick(() => onShowTerms())}
                    className="ds-sidebar-item w-full text-left px-3 py-2 text-sm rounded-lg whitespace-nowrap"
                  >
                    Terms of Service
                  </button>
                )}
              </>
            )}
          </div>
        </nav>

        {/* Footer Copyright */}
        {!isCollapsed && (
          <div className="px-6 py-3 text-[10px] text-center whitespace-nowrap ds-sidebar-muted" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            © 2025 CraftMyParty
          </div>
        )}

        {/* User Section / Footer Actions */}
        {isAuthenticated && (
          <div className="flex flex-col px-2 py-3 mt-auto" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-3 px-3 py-2 mb-2 rounded-lg bg-white/5">
                <div className="w-8 h-8 bg-gradient-to-br from-amber-600 to-stone-700 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  {user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                {!isCollapsed && (
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium whitespace-normal break-words" style={{ color: 'rgba(255,255,255,0.9)' }}>
                        {user?.name || user?.email || 'User'}
                      </div>
                      <div className="text-xs whitespace-normal break-words ds-sidebar-muted">{user?.email}</div>
                    </div>
                )}
            </div>
                         <button
              onClick={() => {
                handleNavClick(() => onNavigate?.('home'));
              }}
              className={`ds-sidebar-item w-full flex items-center gap-3 py-2.5 group ${currentView === 'home' ? 'active' : ''} ${isCollapsed ? 'pl-3' : 'pl-3 pr-3'}`}
              title={isCollapsed ? 'Explore Homepage' : undefined}
              aria-label="Explore Homepage"
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9-9c1.657 0 3 4.03 3 9s-1.343 9-3 9m0-18c-1.657 0-3 4.03-3 9s1.343 9 3 9m-9-9h18" />
              </svg>
              <CollapsedTooltip label="Explore Homepage" />
              {!isCollapsed && <span className="flex-1 text-left whitespace-nowrap">Explore Homepage</span>}
            </button>
            <button
              onClick={() => {
                handleNavClick('billing');
              }}
              className={`ds-sidebar-item w-full flex items-center gap-3 py-2.5 group ${currentView === 'billing' ? 'active' : ''} ${isCollapsed ? 'pl-3' : 'pl-3 pr-3'}`}
              title={isCollapsed ? 'Billing & Credits' : undefined}
              aria-label="Billing & Credits"
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <CollapsedTooltip label="Billing & Credits" />
              {!isCollapsed && <span className="flex-1 text-left whitespace-nowrap">Billing & Credits</span>}
            </button>
            <button
              onClick={() => { handleNavClick('help'); }}
              className={`ds-sidebar-item w-full flex items-center gap-3 py-2.5 group ${currentView === 'help' ? 'active' : ''} ${isCollapsed ? 'pl-3' : 'pl-3 pr-3'}`}
              title={isCollapsed ? 'Help Center' : undefined}
              aria-label="Help Center"
            >
              {getIcon('help')}
              <CollapsedTooltip label="Help Center" />
              {!isCollapsed && <span className="flex-1 text-left whitespace-nowrap">Help Center</span>}
            </button>
            <button
              onClick={() => { handleNavClick('settings'); }}
              className={`ds-sidebar-item w-full flex items-center gap-3 py-2.5 group ${currentView === 'settings' ? 'active' : ''} ${isCollapsed ? 'pl-3' : 'pl-3 pr-3'}`}
              title={isCollapsed ? 'Account Settings' : undefined}
              aria-label="Account Settings"
            >
              {getIcon('settings')}
              <CollapsedTooltip label="Account Settings" />
              {!isCollapsed && <span className="flex-1 text-left whitespace-nowrap">Account Settings</span>}
            </button>
            <button
              onClick={() => {
                if (autoCollapseOnNavigate && onAutoCollapse) onAutoCollapse();
                setShowUserMenu(false);
                onSignOut?.();
              }}
              className={`ds-sidebar-item !text-red-400 hover:!text-red-300 w-full flex items-center gap-3 py-2.5 group ${isCollapsed ? 'pl-3' : 'pl-3 pr-3'}`}
              title={isCollapsed ? 'Sign Out' : undefined}
              aria-label="Sign Out"
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <CollapsedTooltip label="Sign Out" />
              {!isCollapsed && <span className="flex-1 text-left whitespace-nowrap">Sign Out</span>}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
